import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `activity:` scope (F49, `/activity`) —
 * see `apps/web/src/routes/settings/drive/drive.i18n.ts`'s doc comment for
 * the pattern this follows.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/activity.json`.
 * Also OPEN: `auditAction` (`packages/schemas/src/cross.ts`) only has THREE
 * user-actor-able members today (`document.create`/`share.publish`/
 * `comment.create`) — F49's spec copy ("credits purchased/spent, login/
 * session events, plan changes") implies more that don't exist in the
 * closed enum yet. `describeAction` below falls back to the raw action id
 * for anything not in `ACTION_LABEL_KEYS` so a future enum addition never
 * crashes this page, just shows unlabeled copy until translated.
 */
const en = {
  'page.eyebrow': 'Your history',
  'page.title': 'Activity',

  'filter.label': 'Filter by action',
  'filter.all': 'All actions',
  'filter.clear': 'Clear filter',

  'list.column.date': 'Date',
  'list.column.action': 'Action',
  'list.column.target': 'Details',
  'list.empty.title': 'Your activity will appear here',
  'list.empty.description': 'Documents you create, shares you publish, and account changes show up in this feed.',
  'list.noResults.title': 'No activity matches “{query}”',
  'list.error.title': "Couldn't load your activity.",
  'list.searchLabel': 'Search activity',
  'list.searchPlaceholder': 'Search…',
  'list.loadMore': 'Load more',

  'action.retry': 'Retry',
  'target.unavailable': 'No longer available',

  'action.document.create': 'Created a document',
  'action.share.publish': 'Published a share',
  'action.comment.create': 'Posted a comment',
} as const;

const ro: Record<keyof typeof en, string> = {
  'page.eyebrow': 'Istoricul tău',
  'page.title': 'Activitate',

  'filter.label': 'Filtrează după acțiune',
  'filter.all': 'Toate acțiunile',
  'filter.clear': 'Șterge filtrul',

  'list.column.date': 'Dată',
  'list.column.action': 'Acțiune',
  'list.column.target': 'Detalii',
  'list.empty.title': 'Activitatea ta va apărea aici',
  'list.empty.description': 'Documentele pe care le creezi, partajările pe care le publici și modificările de cont apar în acest flux.',
  'list.noResults.title': 'Nicio activitate nu se potrivește cu „{query}”',
  'list.error.title': 'Nu am putut încărca activitatea ta.',
  'list.searchLabel': 'Caută în activitate',
  'list.searchPlaceholder': 'Caută…',
  'list.loadMore': 'Încarcă mai multe',

  'action.retry': 'Reîncearcă',
  'target.unavailable': 'Nu mai este disponibil',

  'action.document.create': 'Ai creat un document',
  'action.share.publish': 'Ai publicat o partajare',
  'action.comment.create': 'Ai postat un comentariu',
};

export const ACTIVITY_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type ActivityCatalogKey = keyof typeof en;
